import React from 'react';
import classNames from 'classnames';
import Select, { OptionTypeBase } from 'react-select';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { emphasize } from '@material-ui/core/styles/colorManipulator';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Chip from '@material-ui/core/Chip';
import CancelIcon from '@material-ui/icons/Cancel';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import styled from 'styled-components';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      height: 250,
      whiteSpace: 'unset',
      wordBreak: 'break-all',
    },
    input: {
      display: 'flex',
      padding: 0,
      height: 'auto',
    },
    valueContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
      alignItems: 'center',
      overflow: 'hidden',
    },
    chip: {
      margin: theme.spacing(0.5, 0.25),
    },
    chipFocused: {
      backgroundColor: emphasize(
        theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
        0.08
      ),
    },
    noOptionsMessage: {
      padding: theme.spacing(1, 2),
    },
    singleValue: {
      fontSize: 16,
    },
    placeholder: {
      position: 'absolute',
      left: 2,
      bottom: 6,
      fontSize: 16,
    },
    paper: {
      position: 'absolute',
      zIndex: 1,
      marginTop: theme.spacing(1),
      left: 0,
      right: 0,
    },
    divider: {
      height: theme.spacing(2),
    },
  })
);

type TWrapper = {
  margin: number;
};

const Wrapper = styled.div<TWrapper>`
  margin-top: ${(props) => props.margin + 'px'};
  margin-bottom: ${(props) => props.margin + 'px'};
`;

// export interface OptionType {
//   label: string;
//   value: string;
// }

interface Props<T> {
  readOnly?: boolean;
  data: Array<T>;
  selected: T | undefined;
  label: string;
  placeholder: string;
  isClearable: boolean;
  onChange: (val: T | undefined) => void;
  optionLabel: (option: T) => string;
  optionValue: (option: T) => string;
  margin?: number;
}

function NoOptionsMessage(props: any) {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }: any) {
  return <div ref={inputRef} {...props} />;
}

function Control(props: any) {
  return (
    <TextField
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props: any) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
        whiteSpace: 'normal',
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props: any) {
  return (
    <Typography color="textSecondary" className={props.selectProps.classes.placeholder} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function SingleValue(props: any) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props: any) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props: any) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classNames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function Menu(props: any) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}
const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

const SelectSingle: <T extends OptionTypeBase>(p: Props<T>) => React.ReactElement<Props<T>> = (props) => {
  const classes = useStyles();
  const theme = useTheme();

  const selectStyles = {
    input: (base: any) => ({
      ...base,
      color: theme.palette.text.primary,
      '& input': {
        font: 'inherit',
      },
    }),
  };

  return (
    <Wrapper margin={props.margin !== undefined ? props.margin : 16}>
      <Select
        isDisabled={props.readOnly}
        classes={classes}
        styles={selectStyles}
        inputId={'react-select-single' + props.label}
        getOptionLabel={props.optionLabel}
        getOptionValue={props.optionValue}
        textFieldProps={{
          label: props.label,
          InputLabelProps: {
            htmlFor: 'react-select-single' + props.label,
            shrink: true,
          },
          placeholder: props.placeholder,
        }}
        options={props.data}
        components={components}
        value={props.selected}
        onChange={(v: any) => props.onChange((v && v instanceof Array ? v[0] : v) || undefined)}
        isClearable={props.isClearable}
      />
    </Wrapper>
  );
};

export default SelectSingle;
